import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Skeleton from '@material-ui/lab/Skeleton';

// CONSTANTS
import { LOCATIONS_HOVER, LOCATIONS_MODAL } from '@/constants/locations';

// UTILITY
import { analyticsAddedToWatchlist } from '@/utility/analytics';
import { updateCarouselOptions } from '@/utility/content';
import { sendParentMessage } from '@/utility/common';

// STORE
import { WatchListActions, ContentActions } from '@/store/actions';

// HOOKS
import useActions from '@/hooks/useActions';

// COMPONENTS
import SectionTitleLink from '@/components/SectionTitleLink';
import StageCarousel from '@/components/Carousels/StageSwiperCarousel';
import { SwiperSlide } from '@/components/Carousels/SwiperCarousel';
import ShowMovieCard, { ShowMovieDetailsDialog } from '@/components/Cards/ShowMovieCard';
import ShowMovieCardSkeleton from '@/components/Skeletons/ShowMovieCardSkeleton';
import Link from '@/components/Link';

// STYLES
import { Container, WhatToWatchWrapper } from './styles';

const carouselOptions = {
  spaceBetween: 30,
  slidesPerView: 'auto',
  watchOverflow: true,
  breakpoints: {
    0: {
      speed: 600,
      slidesPerGroup: 3.2,
      spaceBetween: 8,
    },
    600: {
      speed: 600,
      slidesPerGroup: 3,
    },
    1200: {
      speed: 1000,
      slidesPerGroup: 6,
      allowTouchMove: false,
    },
  },
};

const ShowsMoviesCarousel = props => {
  const {
    data,
    title,
    link,
    stylesType,
    showSeeAll,
    loading,
    analyticsLocation,
    swimlaneAnalytics,
    onUpdateStatus,
    hideTitle,
    hideMobileNav,
  } = props;

  const [t] = useTranslation();
  const { isAuth, user, actionLoadings, partner, isIframe } = useSelector(
    ({ app, partner: partnerStore, auth, watchList, content }) => ({
      isIframe: app.isIframe,
      partner: partnerStore.data,
      isAuth: auth.isAuth,
      user: auth.user,
      actionLoadings: { ...watchList.loadings.actions, ...content.loadings.actions },
    }),
  );

  const [changeContentStatus, openAddToWLDialogState] = useActions([
    (content_id, status, callback) => {
      return ContentActions.changeContentStatus.action({
        content_id,
        status,
        callback,
      });
    },
    dialogProps => {
      return WatchListActions.manageAddToWLDialogState.action({
        state: true,
        props: dialogProps,
      });
    },
  ]);

  const [state, setState] = useState({
    displayDialog: false,
    selectedId: -1,
    selectedData: {},
    carouselOptions: hideMobileNav ? updateCarouselOptions(carouselOptions) : carouselOptions,
    card_index: null,
  });

  const downMd = useMediaQuery(({ breakpoints }) => breakpoints.down('md'));
  const downSm = useMediaQuery(({ breakpoints }) => breakpoints.down('sm'));

  React.useEffect(() => {
    if (hideMobileNav) {
      setState({ ...state, carouselOptions: updateCarouselOptions(carouselOptions) });
    }
  }, [hideMobileNav]);

  const hasSwimlaneAnalytics = !!Object.keys(swimlaneAnalytics).length;

  let titleLinkProps = {};
  let cardLinkProps = {};

  if (hasSwimlaneAnalytics) {
    titleLinkProps = {
      analyticsType: 'swimlane-see-all',
      analyticsProps: {
        ...swimlaneAnalytics,
        type: 'title',
      },
    };
    cardLinkProps = {
      analyticsType: 'swimlane-see-all',
      analyticsProps: {
        ...swimlaneAnalytics,
        type: 'card',
      },
    };
  }

  let updatedStylesType = stylesType;

  function handleChangeWatchList(contentData) {
    function addCallback() {
      const properties = {
        content_id: contentData.id,
        content_name: contentData.title || '',
        user_id: user?.id || '',
        partner_id: partner?.id || '',
        location: state.displayDialog ? LOCATIONS_MODAL : LOCATIONS_HOVER,
        ...swimlaneAnalytics,
      };

      analyticsAddedToWatchlist(properties);
    }

    openAddToWLDialogState({
      data: contentData,
      addCallback,
    });
  }

  function handleChangeContentStatus(id, contentStatus) {
    changeContentStatus(id, contentStatus, () => {
      onUpdateStatus(id, contentStatus);
    });
  }

  const onCardClick = card_index => selectedId => {
    if (isIframe) {
      sendParentMessage({ goToModal: true });
    }

    setState({ ...state, displayDialog: true, selectedId, card_index });
  };

  function onClose() {
    if (isIframe) {
      sendParentMessage({ scrollBack: true });
    }
    setState({ ...state, displayDialog: false, card_index: null });
  }

  const selectedData = data.find(el => el.id === state.selectedId);

  const tooltipProps = {};

  if (downMd && updatedStylesType === 'collections' && !hideMobileNav) {
    updatedStylesType = 'want-to-watch';
  }

  const skeletons = new Array(downSm ? 3 : 6).fill('');
  const skeletonsSection = (
    <>
      {!hideTitle && (
        <Grid item xs={12}>
          <Skeleton width="40%" height={50} />
        </Grid>
      )}
      {skeletons.map((item, index) => {
        return (
          <Grid item xs={4} md={2} key={index}>
            <ShowMovieCardSkeleton />
          </Grid>
        );
      })}
    </>
  );

  return (
    <Container className={clsx('carousel-wtw', `carousel-wtw--${updatedStylesType}`)}>
      {loading ? (
        <Grid container spacing={2}>
          {skeletonsSection}
        </Grid>
      ) : (
        <>
          {data.length ? (
            <>
              <ShowMovieDetailsDialog
                analyticsLocation={analyticsLocation}
                isAuth={isAuth}
                open={state.displayDialog}
                onClose={onClose}
                actionLoading={actionLoadings[state.selectedId]}
                data={selectedData}
                addToWatchList={handleChangeWatchList}
                removeFromWatchList={handleChangeWatchList}
                changeContentStatus={handleChangeContentStatus}
                swimlaneAnalytics={{ ...swimlaneAnalytics, card_index: state.card_index }}
              />
              {!!title && !hideTitle && (
                <SectionTitleLink
                  className="carousel-wtw__title"
                  title={title}
                  link={link}
                  analyticsLocation={analyticsLocation}
                  LinkProps={titleLinkProps}
                />
              )}
              {data.length && (
                <WhatToWatchWrapper>
                  <StageCarousel stylesType={updatedStylesType} carouselOptions={carouselOptions}>
                    {data.map((appData, card_index) => (
                      <SwiperSlide key={card_index}>
                        <ShowMovieCard
                          tooltipProps={tooltipProps}
                          isAuth={isAuth}
                          data={appData}
                          actionLoading={actionLoadings[appData.id]}
                          addToWatchList={handleChangeWatchList}
                          removeFromWatchList={handleChangeWatchList}
                          changeContentStatus={handleChangeContentStatus}
                          onClick={onCardClick(card_index)}
                          partner={partner}
                          analyticsLocation={analyticsLocation}
                          swimlaneAnalytics={{ ...swimlaneAnalytics, card_index }}
                        />
                      </SwiperSlide>
                    ))}
                    {showSeeAll && !!link && data.length >= 6 && (
                      <SwiperSlide className="card-wrapper--see-all">
                        <div className="dummy-card">
                          <Link
                            className="dummy-card__content"
                            to={link}
                            location={analyticsLocation}
                            {...cardLinkProps}
                          >
                            <i className="icon-arrow-right" />
                            <Typography className="p4" component="span">
                              {t('buttons.seeAll')}
                            </Typography>
                          </Link>
                        </div>
                      </SwiperSlide>
                    )}
                  </StageCarousel>
                </WhatToWatchWrapper>
              )}
            </>
          ) : null}
        </>
      )}
    </Container>
  );
};

ShowsMoviesCarousel.defaultProps = {
  data: [],
  title: '',
  link: '',
  stylesType: 'want-to-watch',
  showSeeAll: false,
  loading: false,
  swimlaneAnalytics: {},
  onUpdateStatus: () => {},
  hideTitle: false,
  hideMobileNav: false,
};

ShowsMoviesCarousel.propTypes = {
  data: PropTypes.array,
  title: PropTypes.string,
  link: PropTypes.string,
  analyticsLocation: PropTypes.string,
  stylesType: PropTypes.string,
  showSeeAll: PropTypes.bool,
  loading: PropTypes.bool,
  swimlaneAnalytics: PropTypes.object,
  onUpdateStatus: PropTypes.func,
  hideTitle: PropTypes.bool,
  hideMobileNav: PropTypes.bool,
};

export default React.memo(ShowsMoviesCarousel);
